import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Instagram } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";
import { CartDrawer } from "./CartDrawer";

const NAV = [
  { to: "/", label: "Головна" },
  { to: "/products", label: "Купальники" },
  { to: "/beauty", label: "Догляд" },
  { to: "/about", label: "Про бренд" },
];

export const Layout = ({ children }) => {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const onHome = pathname === "/";
  // White logo/icons only over the hero (home, top, menu closed). Once the
  // mobile menu opens the bar turns cream, so switch them to navy.
  const lightHeader = onHome && !scrolled && !mobileOpen;

  return (
    <div className="min-h-screen flex flex-col bg-silea-cream">
      {/* Header */}
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          mobileOpen
            ? "bg-silea-cream border-b border-stone-200"
            : scrolled || !onHome
              ? "bg-silea-cream/95 backdrop-blur border-b border-stone-200"
              : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link">
            <Logo color={lightHeader ? "#FFFFFF" : "#0A192F"} />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label}`}
                className={`silea-link eyebrow ${
                  lightHeader ? "text-white" : "text-silea-navy"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setOpen(true)}
              data-testid="open-cart-btn"
              className={`relative p-2 ${lightHeader ? "text-white" : "text-silea-navy"}`}
              aria-label="Кошик"
            >
              <ShoppingBag size={20} strokeWidth={1.4} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-0.5 -right-0.5 bg-silea-yellow text-silea-navy text-[10px] font-medium rounded-full h-4 w-4 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className={`md:hidden p-2 ${lightHeader ? "text-white" : "text-silea-navy"}`}
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Меню"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden bg-silea-cream border-t border-stone-200 px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="eyebrow text-silea-navy" data-testid={`mobile-nav-${n.label}`}>
                {n.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      {/* Main */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-silea-navy text-white" data-testid="site-footer">
        {/* Main footer */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo color="#FFFFFF" />
            <p className="mt-6 max-w-md text-white/70 leading-relaxed">
              SILÉA — український бренд купальників з італійською душею.
              Створено для жінок, які знають свою цінність. <br />
              <span className="font-serif-display italic text-white/90">Будь власною музою.</span>
            </p>
          </div>
          <div className="md:col-span-4">
            <h4 className="eyebrow text-white/60 mb-4">Магазин</h4>
            <ul className="space-y-3">
              <li><Link to="/products" className="silea-link" data-testid="footer-link-all">Усі моделі</Link></li>
              <li><Link to="/customizer" className="silea-link" data-testid="footer-link-customizer">Створити власний дизайн</Link></li>
              <li><Link to="/about" className="silea-link" data-testid="footer-link-about">Про бренд</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="eyebrow text-white/60 mb-4">Контакти</h4>
            <ul className="space-y-3 text-white/80">
              <li>
                <a
                  href="mailto:sileabrand@gmail.com"
                  className="silea-link"
                  data-testid="footer-email-link"
                >
                  sileabrand@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+380968946713"
                  className="silea-link"
                  data-testid="footer-phone-link"
                >
                  +380 96 894 67 13
                </a>
              </li>
              <li className="flex items-center gap-2 pt-2">
                <Instagram size={16} strokeWidth={1.4} />
                <a
                  href="https://www.instagram.com/silea_brand/?g=5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="silea-link"
                  data-testid="instagram-link"
                >
                  @silea_brand
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
            <span>© {new Date().getFullYear()} SILÉA. Усі права захищено.</span>
            <span>Виготовлено в Україні · Італійська тканина</span>
          </div>
        </div>
      </footer>

      <CartDrawer />
    </div>
  );
};
